import {Action, FlagProps, showNotification} from 'platform/components';
import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {
  InterestRecommendedVehicleResponseBody,
  useAddMinipurchaseOfferedVehicleMutation,
} from '@dms/api/finance';
import {FullVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  handleApiError,
  refreshDmsDatagrid,
  useInterestEditPermissions,
  usePricePermissions,
  VehicleWidget,
} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {OFFERABLE_VEHICLES_GRID_CODE} from '../../../(sections)/Selling/utils/offerableVehiclesGridCode';
import {useInterestData} from '../../../hooks/useInterestData';
import {getMatchCriterionLabel} from '../../../utils/getMatchCriterionLabel';

type VehicleFlag = Pick<FlagProps, 'colorScheme' | 'label' | 'isSubtle'>;

interface RecommendedVehicleProps extends RequiredTestIdProps {
  interestId: string;
  vehicle: FullVehicleResponseBody;
  recommendation: InterestRecommendedVehicleResponseBody;
}

export function RecommendedVehicle(props: RecommendedVehicleProps) {
  const formatPrice = useFormatCurrency();

  const {canViewAnyOfVehiclePriceFields: canReadPrices} = usePricePermissions({
    vehicleRecordId: null,
    businessCaseRecordId: null,
  });

  const {interest, miniPurchase} = useInterestData(props.interestId);
  const {hasAddMinipurchaseOfferedVehicleInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const [addMinipurchaseOfferedVehicle, {isLoading: isAddOfferedVehicleLoading}] =
    useAddMinipurchaseOfferedVehicleMutation();

  const handleUseVehicle = () => {
    if (isNil(miniPurchase)) {
      return;
    }

    addMinipurchaseOfferedVehicle({
      minipurchaseId: miniPurchase.id,
      addMinipurchaseOfferedVehicleRequestBody: {vehicleId: props.vehicle.id},
    })
      .unwrap()
      .then(() => {
        showNotification.info(i18n.t('entity.interest.actions.addToOfferedVehicle'));
        refreshDmsDatagrid(OFFERABLE_VEHICLES_GRID_CODE);
      })
      .catch(handleApiError);
  };

  const sellingPriceWithVat = props.vehicle.sellingPrice.withVat;
  const priceDifference = props.recommendation.priceDifference;

  const mappedVehicle = {
    id: props.vehicle.id,
    vin: props.vehicle.vin,
    licencePlate: props.vehicle.vehicle.registrationPlate,
    firstRegistration: props.vehicle.vehicle.firstRegistrationOn,
    manufacturedOn: props.vehicle.vehicle.manufacturedOn,
    make: props.vehicle.make,
    vehicleType: props.vehicle.type?.toString(),
    modelFamily: props.vehicle.modelFamily,
    trim: props.vehicle.trim,
    title: props.vehicle.title,
    variant: props.vehicle.variant,
    mileage: props.vehicle.vehicle.mileage,
    transmission: props.vehicle.transmission,
    fuelType: props.vehicle.fuelType,
    bodyStyle: props.vehicle.bodyStyle,
    driveType: props.vehicle.drive,
    photos: props.vehicle.photos,
    // The backend sends the absolute difference, and null when the user may not read the selling price.
    // The advertised vehicle often has exactly the inquiry price; "differs by 0" says nothing
    firstParameter:
      isNotNil(priceDifference) && Number(priceDifference.amount) > 0
        ? i18n.t('entity.interest.vehicleRecommendations.priceDifference', {
            price: formatPrice(Number(priceDifference.amount), priceDifference.currency, 0),
          })
        : null,
    secondParameter:
      canReadPrices && isNotNil(sellingPriceWithVat)
        ? formatPrice(Number(sellingPriceWithVat.amount), sellingPriceWithVat.currency, 0)
        : null,
    actions: buildArray<Action>()
      .add({
        type: 'button',
        variant: 'primary',
        leftIcon: 'content/add_circle',
        title: i18n.t('entity.interest.vehicleRecommendations.useVehicle'),
        isLoading: isAddOfferedVehicleLoading,
        isDisabled:
          isNil(miniPurchase) ||
          isAddOfferedVehicleLoading ||
          !hasAddMinipurchaseOfferedVehicleInterestPermission,
        onClick: handleUseVehicle,
        'data-testid': suffixTestId('useVehicle', props),
      })
      .add({
        type: 'button',
        variant: 'outlined',
        leftIcon: 'action/launch',
        title: i18n.t('entity.vehicle.labels.detail'),
        onClick: () =>
          window.open(
            composePath(vehiclesRoutes.detail, {params: {id: props.vehicle.id}}),
            '_blank'
          ),
        'data-testid': suffixTestId('detail', props),
      }),
  };

  const flags = buildArray<VehicleFlag>()
    .when(props.recommendation.fromAdvertisement, {
      colorScheme: 'purple',
      label: i18n.t('entity.interest.vehicleRecommendations.fromAdvertisement'),
    })
    .add(
      ...props.recommendation.matchedCriteria.map((criterion): VehicleFlag => ({
        colorScheme: 'neutral',
        label: getMatchCriterionLabel(criterion),
        isSubtle: true,
      }))
    );

  return (
    <VehicleWidget
      isBoxed
      data-testid={suffixTestId('vehicleWidget', props)}
      vehicle={mappedVehicle}
      flags={flags}
      currentInterestState={interest?.state}
    />
  );
}
